import React, { useEffect, useState, useCallback } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { fetchPublicArtifacts, fetchSecurityAlerts } from "../api/client";
import { PublicArtifact, SecurityAlert } from "../types";
import DataTable from "../components/DataTable";
import ExportButton from "../components/ExportButton";
import StatCard from "../components/StatCard";
import DetailModal from "../components/DetailModal";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

function SeverityBadge({ severity }: { severity: string }) {
  const s = severity.toLowerCase();
  const cls =
    s === "high"
      ? "bg-red-100 text-red-700 border-red-200"
      : s === "medium"
      ? "bg-orange-100 text-orange-700 border-orange-200"
      : s === "low"
      ? "bg-yellow-100 text-yellow-700 border-yellow-200"
      : "bg-gray-100 text-gray-500 border-gray-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${cls}`}>
      {severity}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = status.toLowerCase();
  const cls =
    s === "resolved"
      ? "bg-green-100 text-green-700 border-green-200"
      : s === "dismissed"
      ? "bg-gray-100 text-gray-500 border-gray-200"
      : "bg-blue-100 text-blue-700 border-blue-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border capitalize ${cls}`}>
      {status}
    </span>
  );
}

type ActiveTab = "public-reports" | "security-alerts";

function PublicReportsTab() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [artifacts, setArtifacts] = useState<PublicArtifact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (activeOnly) params.active_only = "true";

    fetchPublicArtifacts(params)
      .then((data) => setArtifacts(data))
      .catch((err) => setError(err.message ?? "Failed to load public artifacts."))
      .finally(() => setLoading(false));
  }, [activeOnly]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOnly]);

  const publishedCount = artifacts.filter((a) => a.shareType === "publishedToWeb").length;
  const orgWideCount = artifacts.filter((a) => a.shareType === "orgWideLink").length;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<PublicArtifact, any>[] = [
    { accessorKey: "reportName", header: "Report Name", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "workspaceName", header: "Workspace", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "shareType",
      header: "Share Type",
      cell: ({ getValue }) => {
        const v = getValue();
        return v === "publishedToWeb" ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-red-100 text-red-700 border-red-200">Public Web</span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-orange-100 text-orange-700 border-orange-200">Org-Wide</span>
        );
      },
    },
    {
      accessorKey: "detectedAt",
      header: "Detected",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      accessorKey: "isActive",
      header: "Status",
      cell: ({ getValue }) => {
        const active = getValue() as boolean;
        return active ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-green-100 text-green-700 border-green-200">Active</span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border bg-gray-100 text-gray-500 border-gray-200">Inactive</span>
        );
      },
    },
    {
      accessorKey: "lastSeenAt",
      header: "Last Seen",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const url = row.original.embedUrl;
        if (!url) return <span className="text-gray-300">—</span>;
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
            </svg>
            Open
          </a>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="grid grid-cols-2 gap-4 max-w-sm">
          <StatCard label="Published to Web" value={publishedCount} severity={publishedCount > 0 ? "critical" : "normal"} />
          <StatCard label="Shared Org-Wide" value={orgWideCount} severity={orgWideCount > 0 ? "critical" : "normal"} />
        </div>
        <ExportButton
          endpoint="/api/export/public-artifacts"
          params={{ active_only: activeOnly ? "true" : undefined }}
          filename="public-artifacts-export.csv"
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          id="activeOnly"
          type="checkbox"
          checked={activeOnly}
          onChange={(e) => setActiveOnly(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <label htmlFor="activeOnly" className="text-sm text-gray-600">Show active reports only</label>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading public artifacts…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && artifacts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-green-600 gap-3">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-lg font-semibold">No publicly shared reports detected</p>
          <p className="text-sm text-gray-400">All Power BI reports are properly scoped.</p>
        </div>
      )}

      {!loading && !error && artifacts.length > 0 && (
        <DataTable data={artifacts} columns={columns} initialPageSize={25} />
      )}
    </div>
  );
}

function SecurityAlertsTab() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [actionsAlert, setActionsAlert] = useState<SecurityAlert | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [severity, setSeverity] = useState("");
  const [alertStatus, setAlertStatus] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (severity) params.severity = severity;
    if (alertStatus) params.status = alertStatus;

    fetchSecurityAlerts(params)
      .then((data) => setAlerts(data))
      .catch((err) => setError(err.message ?? "Failed to load security alerts."))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, severity, alertStatus]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<SecurityAlert, any>[] = [
    {
      // `alertDate` is a date-only GSI key. Rendering it through a datetime
      // formatter parsed it as UTC midnight, so every row read "10:00:00 AM"
      // in UTC+10. `createdAt` carries the actual instant.
      accessorKey: "createdAt",
      header: "Date / Time",
      cell: ({ row }) => formatDate(row.original.createdAt || row.original.alertDate),
    },
    { accessorKey: "title", header: "Title", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "severity",
      header: "Severity",
      cell: ({ getValue }) => <SeverityBadge severity={getValue() || "—"} />,
    },
    { accessorKey: "serviceSource", header: "Service Source", cell: ({ getValue }) => getValue() || "—" },
    { accessorKey: "userUpn", header: "User", cell: ({ getValue }) => getValue() || "—" },
    {
      accessorKey: "relevanceReason",
      header: "Why In Scope",
      cell: ({ getValue }) => (
        <span className="text-xs text-content-muted">{getValue() || "—"}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => <StatusBadge status={getValue() || "—"} />,
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const alert = row.original;
        if (!alert.recommendedActions) return <span className="text-gray-300">—</span>;
        // This was a `span` styled as a link, with a cursor-pointer and a
        // hover underline, and no click handler at all — the recommended
        // actions were reachable only through the browser's own tooltip,
        // which truncates multi-paragraph text, never appears on a touch
        // device, and is not reachable from the keyboard. So the column
        // advertised something it did not do.
        return (
          <button
            onClick={() => setActionsAlert(alert)}
            className="text-xs text-blue-600 hover:underline"
          >
            Actions
          </button>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Severity</label>
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
            <option value="informational">Informational</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-gray-500">Status</label>
          <select
            value={alertStatus}
            onChange={(e) => setAlertStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>
        <button
          onClick={fetchData}
          className="px-4 py-1.5 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 transition-colors"
        >
          Apply
        </button>
        <div className="ml-auto">
          <ExportButton
            endpoint="/api/export/security-alerts"
            params={{ date_from: dateFrom || undefined, date_to: dateTo || undefined, severity: severity || undefined }}
            filename="security-alerts.csv"
          />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <svg className="w-6 h-6 animate-spin mr-2" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          Loading security alerts…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={fetchData} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {!loading && !error && alerts.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-green-600 gap-3">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-lg font-semibold">No security alerts found</p>
        </div>
      )}

      {!loading && !error && alerts.length > 0 && (
        <DataTable data={alerts} columns={columns} initialPageSize={25} />
      )}

      {actionsAlert && (
        <DetailModal
          title={actionsAlert.title || "Recommended actions"}
          subtitle={[
            actionsAlert.severity?.toUpperCase(),
            actionsAlert.status,
            formatDate(actionsAlert.createdAt),
          ]
            .filter(Boolean)
            .join(" · ")}
          width="lg"
          onClose={() => setActionsAlert(null)}
        >
          <div className="space-y-4 text-sm">
            {actionsAlert.description && (
              <div>
                <h3 className="mb-1 font-semibold text-content">What happened</h3>
                <p className="whitespace-pre-wrap text-content-muted">
                  {actionsAlert.description}
                </p>
              </div>
            )}
            <div>
              <h3 className="mb-1 font-semibold text-content">Recommended actions</h3>
              <p className="whitespace-pre-wrap text-content-muted">
                {actionsAlert.recommendedActions}
              </p>
            </div>
          </div>
        </DetailModal>
      )}
    </div>
  );
}

export default function RiskPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("public-reports");

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Risk</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Public exposure in monitored workspaces and security alert monitoring.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab("public-reports")}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "public-reports"
              ? "border-blue-700 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Public Reports
        </button>
        <button
          onClick={() => setActiveTab("security-alerts")}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "security-alerts"
              ? "border-blue-700 text-blue-700"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Security Alerts
        </button>
      </div>

      {activeTab === "public-reports" && <PublicReportsTab />}
      {activeTab === "security-alerts" && <SecurityAlertsTab />}
    </div>
  );
}
